import React, { useState, useEffect } from 'react';
import { X, Download, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';

interface PDFViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  dealId?: string;
  bucketName?: string;
  filePath?: string;
  title?: string;
  translations?: any;
}

export const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  isOpen,
  onClose,
  pdfUrl: initialPdfUrl,
  dealId,
  bucketName = 'deal_contracts',
  filePath,
  title = 'View Document',
  translations = {
    close: 'Close',
    download: 'Download',
    loading: 'Loading document...',
    refresh: 'Refresh URL',
    error: 'Error loading document. Try refreshing.'
  }
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(initialPdfUrl);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get a fresh signed URL on mount or when isOpen changes
  useEffect(() => {
    if (isOpen && bucketName && filePath) {
      refreshSignedUrl();
    } else if (isOpen) {
      // If we don't have bucket/path but have a URL, try to use it directly
      setPdfUrl(initialPdfUrl);
      setError(null);
    }
  }, [isOpen]);

  const refreshSignedUrl = async () => {
    if (!bucketName || !filePath) {
      console.warn('Cannot refresh URL: missing bucketName or filePath');
      return;
    }

    setIsRefreshing(true);
    setError(null);
    
    try {
      // Try to get a fresh signed URL
      const { data, error } = await supabase
        .storage
        .from(bucketName)
        .createSignedUrl(filePath, 3600); // 1 hour expiry
      
      if (error) throw error;
      
      if (data?.signedUrl) {
        console.log('Successfully refreshed signed URL');
        setPdfUrl(data.signedUrl);
        setIsLoading(true); // Reset loading state for new URL
      } else {
        throw new Error('No signed URL returned');
      }
    } catch (err) {
      console.error('Error refreshing signed URL:', err);
      setError(translations.error || 'Error loading document. Try refreshing.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDownload = async () => {
    // For download, open PDF in new tab
    if (bucketName && filePath) {
      try {
        const { data, error } = await supabase
          .storage
          .from(bucketName)
          .createSignedUrl(filePath, 30); // Short expiry for download
        
        if (error) throw error;
        
        if (data?.signedUrl) {
          // Open in new tab
          window.open(data.signedUrl, '_blank');
        }
      } catch (err) {
        console.error('Error generating download URL:', err);
        alert(translations.error || 'Error generating download link');
      }
    } else if (pdfUrl) {
      // Fallback to using current URL if we don't have bucket/path
      window.open(pdfUrl, '_blank');
    }
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle pressing Escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
      
      // Add special class for mobile fullscreen
      document.documentElement.classList.add('pdf-viewer-open');
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = ''; // Restore scrolling when modal is closed
      
      // Remove special class for mobile fullscreen
      document.documentElement.classList.remove('pdf-viewer-open');
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleCloseModal}
      >
        <motion.div
          className="relative bg-white rounded-lg shadow-xl w-full h-full sm:w-[95%] sm:h-[90%] max-w-5xl flex flex-col"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', bounce: 0.25 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b">
            <h3 className="text-md sm:text-lg font-medium truncate">{title}</h3>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <button
                onClick={handleDownload}
                className="p-1 sm:p-2 rounded-full hover:bg-gray-100 text-gray-700"
                title={translations.download}
              >
                <Download className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button
                onClick={onClose}
                className="p-1 sm:p-2 rounded-full hover:bg-gray-100 text-gray-700"
                title={translations.close}
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </div>
          
          {/* PDF Viewer */}
          <div className="flex-1 w-full h-full bg-gray-100 overflow-auto">
            {isLoading && !error && (
              <div className="h-full w-full flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-block animate-spin h-6 w-6 sm:h-8 sm:w-8 border-4 border-gray-300 border-t-black rounded-full mb-2"></div>
                  <p className="text-sm sm:text-base">{translations.loading}</p>
                </div>
              </div>
            )}
            
            {error && (
              <div className="h-full w-full flex items-center justify-center">
                <div className="text-center p-4">
                  <p className="text-red-500 mb-4 text-sm sm:text-base">{error}</p>
                  <button
                    onClick={refreshSignedUrl}
                    disabled={isRefreshing}
                    className="px-3 py-2 sm:px-4 sm:py-2 bg-[#0f6cbd] text-white rounded-lg inline-flex items-center disabled:bg-blue-400 text-sm sm:text-base"
                  >
                    {isRefreshing ? (
                      <>
                        <div className="inline-block animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        {translations.loading}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                        {translations.refresh}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {!error && (
              <object 
                data={pdfUrl} 
                className="w-full h-full"
                style={{ height: '100%', width: '100%', border: 'none', display: 'block' }}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setError(translations.error || 'Error loading document. Try refreshing.');
                }}
                type="application/pdf"
              >
                <div className="flex items-center justify-center h-full">
                  <p className="text-red-500 mb-4">
                    Il browser non supporta la visualizzazione di PDF integrati.{' '}
                    <a 
                      href={pdfUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      Apri in una nuova scheda
                    </a>
                  </p>
                </div>
              </object>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PDFViewerModal;